/**
 * Everything the command palette can search: the navigation index built from the menu,
 * and the entity sources (courses, clients, chauffeurs...) reachable from a single input.
 *
 * A query is routed by its shape (an email hits the users search, a short number hits the
 * bookings one...) so a keystroke never fans out to every endpoint at once. A `prefix:`
 * scopes the search to one source explicitly.
 */

import { getMenu, MyMenuItem } from '../menu-drawer/menu-drawer';
import { paths } from '../../routes/paths';
import CoreAPIService, {
    getListCompaniesURL,
    getListDriversURL,
    getListPartnersURL,
    getListRidersURL,
} from '../../utils/core-api';
import StringUtils, { REGEX_EMAIL, REGEX_PHONE } from '../../utils/stringUtils';
import { API_QUERY_PLACEHOLDER } from '../../utils/constants';
import defaultTheme from '../../assets/styles/theme';
import envConfig from '../../config';

// Results kept per source: the palette is a shortcut, not a listing page
export const RESULTS_PER_SOURCE = 4;
export const NAVIGATION_RESULTS = 6;

export interface PaletteResult {
    key: string;
    title: string;
    subtitle?: string;
    to: string;
    icon?: string; // set for navigation results, which each carry their own menu icon
}

export interface NavigationEntry {
    to: string;
    label: string;
    group?: string;
    icon?: string;
    color?: string;
}

export enum QueryKind {
    Email = 'email',
    Phone = 'phone',
    Number = 'number',
    Text = 'text',
}

export interface ParsedQuery {
    term: string;
    kind: QueryKind;
    sourceId?: string; // set when the user scoped the search with a prefix
    navigationOnly: boolean;
}

export interface PaletteSource {
    id: string;
    prefix: string; // typed as "<prefix>:" to search this source only
    labelKey: string;
    icon: string;
    color: string;
    minLength: number;
    kinds: QueryKind[]; // query shapes this source answers when no prefix is given
    menuPath: string; // only searched when the matching page is reachable from the menu
    search: (term: string, kind: QueryKind) => Promise<any>;
    format: (row: any, t: any) => PaletteResult;
}

//===========================================//
//=============== Navigation ===============//
//===========================================//

/**
 * Flatten the drawer menu into a searchable list. `getMenu` already drops what the
 * connected user has no right to see, so the palette inherits that filtering for free.
 */
export function getNavigationEntries(t: any): NavigationEntry[] {
    const entries: NavigationEntry[] = [];

    getMenu(t).forEach((item: MyMenuItem) => {
        if (item.to) {
            entries.push({ to: item.to, label: item.label, icon: item.icon, color: item.color });
        }
        item.items?.forEach((sub) =>
            sub.subItems.forEach((subItem) => {
                if (subItem.to) {
                    entries.push({
                        to: subItem.to,
                        label: subItem.label,
                        group: item.label,
                        icon: subItem.icon,
                        color: item.color,
                    });
                }
            }),
        );
    });

    return entries;
}

export function matchNavigation(entries: NavigationEntry[], term: string, limit: number): NavigationEntry[] {
    const needle = StringUtils.normalizeForSearch(term);
    if (!needle) {
        return [];
    }
    const tokens = needle.split(/\s+/).filter(Boolean);

    return entries
        .map((entry) => {
            const label = StringUtils.normalizeForSearch(entry.label);
            const haystack = `${label} ${StringUtils.normalizeForSearch(entry.group ?? '')}`;
            if (!tokens.every((token) => haystack.includes(token))) {
                return null;
            }
            // A label starting with the query beats one merely containing it, itself
            // beating an entry matched only through its menu group
            const score = label.startsWith(needle) ? 0 : label.includes(needle) ? 1 : 2;
            return { entry, score };
        })
        .filter(Boolean)
        .sort((a, b) => a.score - b.score)
        .slice(0, limit)
        .map((match) => match.entry);
}

//========================================//
//=============== Sources ===============//
//========================================//

const privapiGet = (endpoint: string): Promise<any> =>
    CoreAPIService.get(
        `${endpoint}&pagination[num_per_page]=${RESULTS_PER_SOURCE}&pagination[page]=1`,
        null,
        null,
        envConfig.privapi_url,
    );

// `calculate_total=0`: the palette shows the first hits and no pager, so the API can skip
// the count query it would otherwise run over the same rows.
const listGet = (url: string, term: string): Promise<any> =>
    CoreAPIService.get(`${url.replace(API_QUERY_PLACEHOLDER, encodeURIComponent(term))}&calculate_total=0`);

const idSubtitle = (row: any): string => `ID: ${row.id}`;

// The name searches of core-api match on the fullname only, so an email or a phone needs
// the key its listing page filters on: the very keys the email and phone columns send.
const contactURL = (list: string, key: string): string =>
    `/list/${list}?sort=id&order=desc&page=1&search[${key}]=${API_QUERY_PLACEHOLDER}`;

const RIDERS_CONTACT_URLS = {
    [QueryKind.Email]: contactURL('riders', 'email'),
    [QueryKind.Phone]: contactURL('riders', 'phone_number'),
};
const DRIVERS_CONTACT_URLS = {
    [QueryKind.Email]: contactURL('drivers', 'email'),
    [QueryKind.Phone]: contactURL('drivers', 'phone_number'),
};

export const paletteSources: PaletteSource[] = [
    {
        id: 'bookings',
        prefix: '#',
        labelKey: 'source_bookings',
        icon: 'navigation',
        color: defaultTheme.palette.colors.bookings,
        minLength: 4, // same threshold as the number filter of the bookings listing
        kinds: [QueryKind.Number],
        menuPath: paths.bookings,
        search: (term) => listGet(`/list/bookings?page=1&search[number]=${API_QUERY_PLACEHOLDER}`, term),
        format: (row, t) => ({
            key: `bookings-${row.id}`,
            title: t('booking_number', { number: row.number }),
            subtitle: [
                StringUtils.userFullname(row.rider),
                row.company?.name,
                row.status ? t(`booking:booking_status_${row.status}`) : null,
            ]
                .filter(Boolean)
                .join(' · '),
            to: `${paths.bookings}/${row.id}`,
        }),
    },
    {
        id: 'reservations',
        prefix: '$',
        labelKey: 'source_reservations',
        icon: 'date_range',
        color: defaultTheme.palette.colors.reservations,
        minLength: 4,
        kinds: [QueryKind.Number],
        menuPath: paths.reservations,
        search: (term) => listGet(`/list/reservations?page=1&search[number]=${API_QUERY_PLACEHOLDER}`, term),
        format: (row, t) => ({
            key: `reservations-${row.id}`,
            title: t('reservation_number', { number: row.number }),
            subtitle: [
                StringUtils.userFullname(row.rider),
                row.company?.name,
                row.status ? t(`reservation:status.${row.status}`) : null,
            ]
                .filter(Boolean)
                .join(' · '),
            to: `${paths.reservations}/${row.id}`,
        }),
    },
    {
        id: 'users',
        prefix: 'u',
        labelKey: 'source_users',
        icon: 'person',
        color: defaultTheme.palette.colors.riders,
        minLength: 3,
        kinds: [QueryKind.Email, QueryKind.Phone],
        menuPath: paths.users,
        search: (term, kind) => {
            const key = kind === QueryKind.Email ? 'email' : kind === QueryKind.Phone ? 'phone' : 'lastname';
            return privapiGet(`internal/user/list?search[${key}]=${encodeURIComponent(term)}`);
        },
        format: (row) => ({
            key: `users-${row.id}`,
            title: StringUtils.userFullname(row),
            subtitle: [row.email?.[0] ?? row.email, row.phone?.[0] ?? row.phone, row.company?.name]
                .filter(Boolean)
                .join(' · '),
            to: `${paths.users}/${row.id}`,
        }),
    },
    {
        id: 'riders',
        prefix: 'r',
        labelKey: 'source_riders',
        icon: 'people',
        color: defaultTheme.palette.colors.riders,
        minLength: 2,
        kinds: [QueryKind.Text, QueryKind.Email, QueryKind.Phone],
        menuPath: paths.riders,
        search: (term, kind) => listGet(RIDERS_CONTACT_URLS[kind] ?? getListRidersURL(), term),
        format: (row) => ({
            key: `riders-${row.id}`,
            title: StringUtils.userFullname(row),
            subtitle: [row.email, row.phone_number].filter(Boolean).join(' · ') || idSubtitle(row),
            to: `${paths.riders}/${row.id}`,
        }),
    },
    {
        id: 'drivers',
        prefix: 'd',
        labelKey: 'source_drivers',
        icon: 'supervised_user_circle',
        color: defaultTheme.palette.colors.drivers,
        minLength: 2,
        kinds: [QueryKind.Text, QueryKind.Email, QueryKind.Phone],
        menuPath: paths.drivers,
        // The name search matches on name *or* id, so "d:1234" resolves a driver id too
        search: (term, kind) => listGet(DRIVERS_CONTACT_URLS[kind] ?? getListDriversURL(), term),
        format: (row) => ({
            key: `drivers-${row.id}`,
            title: StringUtils.userFullname(row),
            subtitle: [row.phone_number, row.partner?.name].filter(Boolean).join(' · ') || idSubtitle(row),
            to: `${paths.drivers}/${row.id}`,
        }),
    },
    {
        id: 'companies',
        prefix: 'c',
        labelKey: 'source_companies',
        icon: 'business',
        color: defaultTheme.palette.colors.companies,
        minLength: 2,
        kinds: [QueryKind.Text],
        menuPath: paths.companies,
        search: (term) => listGet(getListCompaniesURL(), term),
        format: (row) => ({
            key: `companies-${row.id}`,
            title: row.name,
            subtitle: idSubtitle(row),
            to: `${paths.companies}/${row.id}`,
        }),
    },
    {
        id: 'partners',
        prefix: 'p',
        labelKey: 'source_partners',
        icon: 'emoji_transportation',
        color: defaultTheme.palette.colors.partners,
        minLength: 2,
        kinds: [QueryKind.Text],
        menuPath: paths.partners,
        search: (term) => listGet(getListPartnersURL(), term),
        format: (row) => ({
            key: `partners-${row.id}`,
            title: row.name,
            subtitle: idSubtitle(row),
            to: `${paths.partners}/${row.id}`,
        }),
    },
];

/**
 * Sources the connected user may query, ie the ones whose listing page their menu offers.
 * `getMenu` already applies the account type and the rights of each page.
 */
export function getAvailableSources(navigationEntries: NavigationEntry[]): PaletteSource[] {
    const reachable = navigationEntries.map((entry) => entry.to);
    return paletteSources.filter((source) => reachable.includes(source.menuPath));
}

//======================================//
//=============== Query ===============//
//======================================//

const PREFIX_REGEX = /^([a-z]{1,2}):\s*(.*)$/i;
const LETTER_PREFIX = /^[a-z]+$/i;

/** How a prefix is typed: a letter one needs its colon ("d:"), a sigil stands alone ("#"). */
export const formatPrefix = (prefix: string): string => (LETTER_PREFIX.test(prefix) ? `${prefix}:` : prefix);

/**
 * Drop the separators of a typed phone number: nobody stores "06 12 34 56 78", so the
 * number has to be searched as one run of digits.
 */
export const compactPhone = (term: string): string => term.replace(/[\s.\-/]/g, '');

function getQueryKind(term: string): QueryKind {
    if (REGEX_EMAIL.test(term)) {
        return QueryKind.Email;
    }
    const compact = compactPhone(term);
    // REGEX_PHONE also accepts short numbers, keep those for the ride number search
    if (REGEX_PHONE.test(compact) && compact.replace(/\D/g, '').length >= 9) {
        return QueryKind.Phone;
    }
    if (/^[a-z]{0,3}[-_]?\d{4,}$/i.test(term)) {
        return QueryKind.Number;
    }
    return QueryKind.Text;
}

export function parseQuery(raw: string): ParsedQuery {
    const trimmed = (raw || '').trim();

    // "> something" restricts the palette to the menu
    if (trimmed.startsWith('>')) {
        const term = trimmed.slice(1).trim();
        return { term, kind: QueryKind.Text, navigationOnly: true };
    }

    let term = trimmed;
    let sourceId: string;

    const sigilSource = paletteSources.find((s) => !LETTER_PREFIX.test(s.prefix) && trimmed.startsWith(s.prefix));
    if (sigilSource) {
        term = trimmed.slice(sigilSource.prefix.length).trim();
        sourceId = sigilSource.id;
    } else {
        const match = PREFIX_REGEX.exec(trimmed);
        const source = match && paletteSources.find((s) => s.prefix === match[1].toLowerCase());
        if (source) {
            term = match[2].trim();
            sourceId = source.id;
        }
    }

    return { term, kind: getQueryKind(term), sourceId, navigationOnly: false };
}

/** Both list shapes the searched endpoints answer with: `{ data: [] }` or a bare array. */
export function getResultRows(response: any): any[] {
    const payload = response?.payload ?? response;
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    return [];
}
